// General
import { useEffect } from "react";
// Services
import {
  useLazyGetProfileQuery,
  usePurchaseProductMutation,
} from "../../../services/data.service";
// Static Data
import paymentConst from "../../../const/paymentConst";
// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  updatePaymentErrorInfo,
  updatePaymentState,
  updateSecurionPayTokenDetails,
} from "../../../redux/store/paymentStore";
import { updateVideoCallPrejoinPaymentPassthrough } from "../../../redux/store/privateCallStore";
import {
  updatePaymentProcessDialog,
  updatePaymentErrorDialog,
  updateBuyCoinsDialog,
  updateThankYouForPurchaseDialog,
  updateCoinsPackageDialog,
  updateAddPaymentMethodDialog,
} from "../../../redux/store/dialogStore";
import { updateErrorToast } from "../../../redux/store/toastStore";
// react-gtm-module
import TagManager from "react-gtm-module";

// TODO: Redo this hook
// Should not have any API calling in hook, making things complex
const useSecurionCoinsPayment = () => {
  // API variables
  const [
    getProfile,
    {
      data: getProfileData,
      error: getProfileErrorData,
      isFetching: getProfileFetching,
      isLoading: getProfileLoading,
      isSuccess: getProfileSuccess,
      isError: getProfileError,
    },
  ] = useLazyGetProfileQuery();
  const [
    purchaseProduct,
    {
      data: purchaseProductData,
      error: purchaseProductErrorData,
      isLoading: purchaseProductLoading,
      isSuccess: purchaseProductSuccess,
      isError: purchaseProductError,
    },
  ] = usePurchaseProductMutation();

  // Redux variables
  const cardDetail = useSelector((state) => state.payment.cardDetail);
  const securionPayTokenDetails = useSelector(
    (state) => state.payment.securionPayTokenDetails
  );
  const selectedCoinPackage = useSelector(
    (state) => state.products.selectedCoinPackage
  );
  const promoCode = useSelector((state) => state.products.promoCode);
  const buyCoinsDialog = useSelector((state) => state.dialog.buyCoinsDialog);
  const dispatch = useDispatch();

  // Lifecycle | Mounted
  useEffect(() => {
    if (!buyCoinsDialog) return;

    getProfile(null, true);
  }, [buyCoinsDialog]);

  // Lifecycle | Check for update | Purchase Product API Response
  useEffect(() => {
    if (purchaseProductLoading) {
    } else if (purchaseProductSuccess) {
      // Close loading dialog
      dispatch(updatePaymentProcessDialog(false));

      switch (purchaseProductData?.status) {
        case 0:
          TagManager.dataLayer({
            dataLayer: {
              event: "PWA-CoinPurchase-success", // Do not change this, it will affect GTM and Insider
              category: "Purchase",
              action: "Quick Purchase Coins Actions",
              label: "Quick Purchase Coins Payment State - success",
              value: {},
              currency: purchaseProductData?.data?.paid_currency,
              product_name: purchaseProductData?.data?.product_description,
              user_id: getProfileData?.data?.id_int,
              username: getProfileData?.data?.username,
              amount: purchaseProductData?.data?.paid_amount,
            },
          });

          // Close buy coin dialog
          dispatch(updateBuyCoinsDialog(false));
          // Close coins package dialog
          dispatch(updateCoinsPackageDialog(false));
          // Close Add Payment Method Dialog
          dispatch(updateAddPaymentMethodDialog(false));
          // Open thank you dialog
          dispatch(updateThankYouForPurchaseDialog(true));

          // Notify Pre Join API to refetch data
          dispatch(updateVideoCallPrejoinPaymentPassthrough({}));
          break;
        case -1:
          const errorObj = {
            title: purchaseProductData?.data?.default_error_message?.title,
            message: purchaseProductData?.message,
            subtitle:
              purchaseProductData?.data?.default_error_message?.sub_title,
            solutions:
              purchaseProductData?.data?.default_error_message?.solutions,
            cta: purchaseProductData?.data?.default_error_message?.cta,
          };
          dispatch(updatePaymentErrorInfo(errorObj));

          // Open payment error dialog
          dispatch(updatePaymentErrorDialog(true));
          break;
        default:
          break;
      }

      if (purchaseProductData?.status === 0) {
        // Close buy coin dialog
        dispatch(updateBuyCoinsDialog(false));
        // Close coins package dialog
        dispatch(updateCoinsPackageDialog(false));
        // Open thank you dialog
        dispatch(updateThankYouForPurchaseDialog(true));

        // Notify Pre Join API to refetch data
        dispatch(updateVideoCallPrejoinPaymentPassthrough({}));
      } else if (purchaseProductData?.status === -1) {
      }
    } else if (purchaseProductError) {
    }
  }, [purchaseProductLoading, purchaseProductSuccess, purchaseProductError]);

  // Main Function
  const securionPaymentFlow = () => {
    submitPayment();
  };

  // Part 1 | Call SecurionPay that we are going to make a custom form payment
  const submitPayment = () => {
    // Open loading dialog
    dispatch(updatePaymentProcessDialog(true));

    const form = document.getElementById("payment-form");

    Securionpay.setPublicKey(process.env["REACT_APP_SP_PUBLIC_KEY"]);
    Securionpay.createCardToken(form, createCardTokenCallback);

    // Prevent default form submission
    return false;
  };

  // Part 2 | SecurionPay informs if payment attempt is available
  const createCardTokenCallback = (token) => {
    dispatch(updateSecurionPayTokenDetails(token));

    if (token?.error) {
      // Close loading dialog
      dispatch(updatePaymentProcessDialog(false));

      // Display error message
      // re-enable form submit button
      const toastObj = {
        message: token?.error?.message,
        autoClose: 10000,
      };
      dispatch(updateErrorToast(toastObj));
    } else {
      let param3Ds = {
        amount:
          selectedCoinPackage?.discounted_amount
            ?.toFixed(2)
            .toString()
            .replace(/\./g, "") ??
          selectedCoinPackage?.amount?.toFixed(2).toString().replace(/\./g, ""),
        currency: selectedCoinPackage?.currency,
        card: token.id,
      };

      // Open frame with 3D Secure process
      Securionpay.setPublicKey(process.env["REACT_APP_SP_PUBLIC_KEY"]);
      Securionpay.verifyThreeDSecure(param3Ds, verifyThreeDSecureCallback);
    }
  };

  // Part 2 | Attempt 3DSecure when SecurionPay says good.
  const verifyThreeDSecureCallback = (token) => {
    if (token?.error) {
      // Close loading dialog
      dispatch(updatePaymentProcessDialog(false));

      // display error message
      // re-enable form submit button

      // Update Payment State to Store
      const paymentObj = {
        attempt: false,
        state: paymentConst.paymentState.decline,
        resultMessage: token.error.message,
        token: null,
      };
      dispatch(updatePaymentState(paymentObj));
    } else {
      // re-enable form submit button

      // Update Payment State to Store
      const paymentObj = {
        state: paymentConst.paymentState.pendingBackendSubmission,
        token: token.id,
      };
      dispatch(updatePaymentState(paymentObj));

      const purchaseObj = {
        payment_gateway: "securionpay",
        payment_token: token.id,
        product_hash_id: selectedCoinPackage?.product_hash_id,
        payment_tag: "pwa-coin-wallet",
        promo_code: promoCode,
      };
      purchaseProduct(purchaseObj);
    }
  };

  return securionPaymentFlow;
};

export default useSecurionCoinsPayment;
